/**
 * @file fab-menu.tsx
 *
 * FAB Menu component adhering to MD3 Expressive.
 *
 * Provides a toggle Floating Action Button (FAB) that expands a list of action items with staggered animation.
 * Complies with MD3 FloatingActionButtonMenu specification with full accessibility support
 * (keyboard navigation, focus management, ARIA roles).
 *
 * @see https://m3.material.io/components/floating-action-button/overview
 */

import { Slot } from "@radix-ui/react-slot";
import {
	AnimatePresence,
	domMax,
	LazyMotion,
	m,
	useReducedMotion,
	useSpring,
	useTransform,
} from "motion/react";
import * as React from "react";
import { cn } from "../../../../lib/utils";
import { Ripple, useRippleState } from "../../../ripple";
import {
	SPRING_TRANSITION,
	SPRING_TRANSITION_FAST,
} from "../../../shared/constants";
import { TouchTarget } from "../../../shared/touch-target";

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens — MD3 FAB Menu Spec
// ─────────────────────────────────────────────────────────────────────────────

const SPRING_NORMAL = { stiffness: 700, damping: 40 } as const;
const SPRING_REDUCED = { stiffness: 10000, damping: 100 } as const;
const FOCUS_DELAY_MS = 50;

const TOGGLE_FAB_COLORS: Record<
	string,
	{
		containerBg: string;
		containerText: string;
		checkedBg: string;
		checkedText: string;
	}
> = {
	primary: {
		containerBg: "bg-m3-primary-container",
		containerText: "text-m3-on-primary-container",
		checkedBg: "bg-m3-primary",
		checkedText: "text-m3-on-primary",
	},
	secondary: {
		containerBg: "bg-m3-secondary-container",
		containerText: "text-m3-on-secondary-container",
		checkedBg: "bg-m3-secondary",
		checkedText: "text-m3-on-secondary",
	},
	tertiary: {
		containerBg: "bg-m3-tertiary-container",
		containerText: "text-m3-on-tertiary-container",
		checkedBg: "bg-m3-tertiary",
		checkedText: "text-m3-on-tertiary",
	},
};

/**
 * Size tokens for ToggleFAB.
 *
 * MD3 Kotlin reference:
 *  - Baseline: 56dp, cornerRadius 16dp → 28dp (fully round)
 *  - Medium: ~80dp (FabMediumTokens.ContainerHeight), cornerRadius 20dp → 40dp
 *  - Large: 96dp, cornerRadius 28dp → 48dp
 * @internal
 */
const TOGGLE_FAB_SIZES: Record<
	"sm" | "md" | "lg",
	{ sizeClass: string; initialRadius: number; finalRadius: number }
> = {
	sm: { sizeClass: "h-14 w-14", initialRadius: 16, finalRadius: 28 },
	md: { sizeClass: "h-20 w-20", initialRadius: 20, finalRadius: 40 },
	lg: { sizeClass: "h-24 w-24", initialRadius: 28, finalRadius: 48 },
};

const MENU_ITEM_STYLES = {
	padding: "ps-4 pe-6",
	gap: "gap-3",
	size: "h-14 min-w-14",
	cornerRadius: 999,
} as const;

const MENU_ITEM_COLORS: Record<string, { bg: string; text: string }> = {
	primary: {
		bg: "bg-m3-primary-container",
		text: "text-m3-on-primary-container",
	},
	secondary: {
		bg: "bg-m3-secondary-container",
		text: "text-m3-on-secondary-container",
	},
	tertiary: {
		bg: "bg-m3-tertiary-container",
		text: "text-m3-on-tertiary-container",
	},
};

const ALIGNMENT_CONTAINER_CLASSES: Record<string, string> = {
	end: "items-end bottom-4 right-4 sm:bottom-6 sm:right-6",
	start: "items-start bottom-4 left-4 sm:bottom-6 sm:left-6",
	center: "items-center bottom-4 left-1/2 -translate-x-1/2 sm:bottom-6",
};

const ALIGNMENT_ITEMS_CLASSES: Record<string, string> = {
	end: "items-end",
	start: "items-start",
	center: "items-center",
};

const ALIGNMENT_TRANSFORM_ORIGIN: Record<string, string> = {
	end: "right",
	start: "left",
	center: "bottom",
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Data interface for each action item in FAB menu.
 *
 * If `label` is omitted, renders as an icon-only item.
 */
export interface FABMenuItemData {
	/** Unique ID used for React keys and focus tracking. */
	id: string;
	/** Label displayed adjacent to icon (optional). Omit for icon-only item. */
	label?: string;
	/** Icon node — typically an SVG icon component. */
	icon: React.ReactNode;
	/** Callback invoked when item is activated (click, Enter, or Space). */
	onClick: () => void;
	/**
	 * When `true`, visually and interactively disables the item.
	 * Uses `aria-disabled` instead of HTML `disabled` to preserve focusability for accessibility.
	 * @default false
	 */
	disabled?: boolean;
	/** Additional CSS class names for item wrapper. */
	className?: string;
}

/**
 * Props controlling the main `FABMenu` component.
 *
 * @remarks
 * FABMenu manages menu open/close lifecycle, focus trapping, keyboard control,
 * and staggered motion transitions. `expanded` state is controlled externally.
 */
export interface FABMenuProps {
	/** Whether the FAB Menu is open (expanded). */
	expanded: boolean;
	/** Callback invoked when Toggle FAB is interacted with or backdrop dismissed. */
	onToggle: (expanded: boolean) => void;
	/** List of action items (MD3 spec recommends 2–6 items). */
	items: FABMenuItemData[];
	/**
	 * MD3 container color role for trigger FAB and menu items.
	 * @default "primary"
	 */
	colorVariant?: "primary" | "secondary" | "tertiary";
	/**
	 * FAB size variant controlling container dimensions per MD3 Extended FAB specification.
	 * - `"sm"` – small FAB: 56dp (replaces deprecated "baseline").
	 * - `"md"` – medium FAB: 80dp.
	 * - `"lg"` – large FAB: 96dp.
	 * @default "sm"
	 */
	size?: "sm" | "md" | "lg";
	/**
	 * Menu items alignment relative to Toggle FAB.
	 * - `"end"`: Items align to right edge (trailing edge, recommended for RTL).
	 * - `"start"`: Items align along left edge.
	 * - `"center"`: Items align vertically centered above FAB.
	 * @default "end"
	 */
	alignment?: "start" | "end" | "center";
	/** Custom CSS className for root component. */
	className?: string;
	/**
	 * When `true`, clicking backdrop overlay closes menu.
	 * @default true
	 */
	closeOnBackdropClick?: boolean;
	/**
	 * When `true`, focus automatically moves to the LAST item (closest above FAB) when expanded.
	 * If `false`, focus moves to the FIRST item in list.

	 * @default true
	 */
	focusLast?: boolean;
	/** Required `aria-label` for ToggleFAB for accessibility. */
	"aria-label"?: string;
}

/**
 * Props for standalone `ToggleFAB` component.
 */
export interface ToggleFABProps {
	/** Whether the button is in expanded/active state. */
	expanded: boolean;
	/** Callback fired when button toggle state changes. */
	onToggle: (expanded: boolean) => void;
	/**
	 * Icon generator function receiving `progress` between `0` -> `1` (collapsed -> expanded).
	 * Useful for icon morphing effects during animation (e.g., plus to close icon).
	 *
	 * @example
	 * ```tsx
	 * icon={(progress) => progress > 0.5 ? <Icon name="close" /> : <Icon name="add" />}
	 * ```
	 */
	icon: (progress: number) => React.ReactNode;
	/** MD3 container color role. @default "primary" */
	colorVariant?: "primary" | "secondary" | "tertiary";
	/** FAB size variant controlling container dimensions. @default "sm" */
	size?: "sm" | "md" | "lg";
	/** Additional CSS class name. */
	className?: string;
	/** Accessible label requirement. Required. */
	"aria-label"?: string;
	/** ID link for aria-controls attribute to reference menu. */
	"aria-controls"?: string;
	/** Component ID. */
	id?: string;
}

/**
 * Props for an individual `FABMenuItem`.
 */
export interface FABMenuItemProps {
	/** Icon node to display. */
	icon: React.ReactNode;
	/** Descriptive label text adjacent to icon. */
	label?: string;
	/** Callback fired when item is clicked. */
	onClick: () => void;
	/** Disables interaction while keeping element focusable in tab order. @default false */
	disabled?: boolean;
	/** Container color role. @default "primary" */
	colorVariant?: "primary" | "secondary" | "tertiary";
	/** Custom CSS className. */
	className?: string;
	/** Item index used for staggered animation delay. */
	index?: number;
	/** Total items count in menu array. */
	totalItems?: number;
	/** Logic `tabIndex` value for manual keyboard navigation. */
	tabIndex?: number;
	/**
	 * Change the component to the underlying child element and merge props/behavior.
	 * Useful when wrapping in Next.js `<Link>` or custom anchor tags.
	 * @default false
	 */
	asChild?: boolean;
	children?: React.ReactNode;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation Variants
// ─────────────────────────────────────────────────────────────────────────────

const ITEM_SPRING = { type: "spring" as const, stiffness: 700, damping: 25 };

const MENU_CONTAINER_VARIANTS = {
	open: { transition: { staggerChildren: 0.033, staggerDirection: 1 } },
	closed: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
} as const;

const MENU_ITEM_VARIANTS = {
	open: { scaleX: 1, opacity: 1, transition: ITEM_SPRING },
	closed: { scaleX: 0, opacity: 0, transition: ITEM_SPRING },
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal Icon Components
// ─────────────────────────────────────────────────────────────────────────────

function AddIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			fill="currentColor"
			width="24"
			height="24"
		>
			<title>Add</title>
			<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 24 24"
			fill="currentColor"
			width="24"
			height="24"
		>
			<title>Close</title>
			<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
		</svg>
	);
}

function defaultFabIcon(progress: number) {
	return progress > 0.5 ? <CloseIcon /> : <AddIcon />;
}

// ─────────────────────────────────────────────────────────────────────────────
// ToggleFAB Component
// ─────────────────────────────────────────────────────────────────────────────

const ToggleFABComponent = React.forwardRef<HTMLButtonElement, ToggleFABProps>(
	(
		{
			expanded,
			onToggle,
			icon,
			colorVariant = "primary",
			size = "sm",
			className,
			id,
			"aria-label": ariaLabel,
			"aria-controls": ariaControls,
		},
		ref,
	) => {
		const prefersReduced = useReducedMotion();
		const colors = TOGGLE_FAB_COLORS[colorVariant] ?? TOGGLE_FAB_COLORS.primary;
		const sizeTokens = TOGGLE_FAB_SIZES[size] ?? TOGGLE_FAB_SIZES.sm;

		const springConfig = prefersReduced ? SPRING_REDUCED : SPRING_NORMAL;
		const checkedProgress = useSpring(expanded ? 1 : 0, springConfig);

		React.useEffect(() => {
			checkedProgress.set(expanded ? 1 : 0);
		}, [expanded, checkedProgress]);

		const borderRadius = useTransform(
			checkedProgress,
			[0, 1],
			[`${sizeTokens.initialRadius}px`, `${sizeTokens.finalRadius}px`],
		);

		const [iconProgress, setIconProgress] = React.useState(expanded ? 1 : 0);

		React.useEffect(() => {
			return checkedProgress.on("change", setIconProgress);
		}, [checkedProgress]);

		const { ripples, onPointerDown, removeRipple } = useRippleState();

		const handleClick = React.useCallback(() => {
			onToggle(!expanded);
		}, [expanded, onToggle]);

		return (
			<m.button
				ref={ref}
				id={id}
				type="button"
				aria-expanded={expanded}
				aria-haspopup="menu"
				aria-label={ariaLabel ?? (expanded ? "Close menu" : "Open menu")}
				aria-controls={ariaControls}
				data-expanded={expanded ? "true" : "false"}
				onClick={handleClick}
				onPointerDown={onPointerDown}
				style={{ borderRadius }}
				animate={{
					boxShadow: expanded
						? "0 4px 8px 3px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.3)"
						: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
				}}
				whileTap={{ scale: 0.95, transition: SPRING_TRANSITION_FAST }}
				transition={{ boxShadow: SPRING_TRANSITION }}
				className={cn(
					"relative shrink-0 inline-flex items-center justify-center",
					"select-none cursor-pointer overflow-hidden",
					"transition-colors duration-200",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-offset-2",
					sizeTokens.sizeClass,
					expanded ? colors.checkedBg : colors.containerBg,
					expanded ? colors.checkedText : colors.containerText,
					className,
				)}
			>
				<TouchTarget />
				<Ripple ripples={ripples} onRippleDone={removeRipple} />
				<span
					aria-hidden="true"
					className="relative z-10 flex items-center justify-center size-6 pointer-events-none"
				>
					{icon(iconProgress)}
				</span>
			</m.button>
		);
	},
);

ToggleFABComponent.displayName = "ToggleFAB";

/**
 * Toggle FAB button (Morphing FAB) usable as standalone or trigger for `FABMenu`.
 *
 * Container shape transitions from squircle to circle (square → circle) and shifts colors
 * when `expanded` state changes from false → true.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = React.useState(false);
 * <ToggleFAB
 *   expanded={open}
 *   onToggle={setOpen}
 *   colorVariant="primary"
 *   aria-label="Toggle actions"
 *   icon={(progress) => progress > 0.5 ? <Icon name="close" /> : <Icon name="add" />}
 * />
 * ```
 */
export const ToggleFAB = React.memo(ToggleFABComponent);

// ─────────────────────────────────────────────────────────────────────────────
// FABMenuItem Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Single action item within a `FABMenu`.
 *
 * Renders a pill shape enclosing icon and optional label text.
 * When `label` is omitted, renders as a square item containing only icon.
 * Includes MD3 Ripple and 48dp touch target area per WCAG 2.5.5 touch target guidelines.
 *
 * @remarks
 * Disabled items use `aria-disabled="true"` rather than native HTML `disabled`
 * so they remain focusable in the tab sequence per MD3 specification.
 */
export function FABMenuItem({
	icon,
	label,
	onClick,
	disabled = false,
	colorVariant = "primary",
	className,
	tabIndex = 0,
	asChild = false,
	children,
}: FABMenuItemProps) {
	const colors = MENU_ITEM_COLORS[colorVariant] ?? MENU_ITEM_COLORS.primary;

	const { ripples, onPointerDown, removeRipple } = useRippleState({ disabled });

	const handleClick = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (disabled) {
				e.preventDefault();
				return;
			}
			onClick();
		},
		[disabled, onClick],
	);

	const handleKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (disabled) return;
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onClick();
			}
		},
		[disabled, onClick],
	);

	const innerContent = (
		<>
			<TouchTarget />
			<Ripple ripples={ripples} onRippleDone={removeRipple} />
			<span
				aria-hidden="true"
				className="relative z-10 flex items-center justify-center size-6 shrink-0 [&>svg]:w-full [&>svg]:h-full pointer-events-none"
			>
				{icon}
			</span>
			{label && (
				<span className="relative z-10 text-base font-medium leading-none pointer-events-none">
					{label}
				</span>
			)}
		</>
	);

	const containerClassName = cn(
		"relative inline-flex flex-row items-center",
		"select-none cursor-pointer overflow-hidden",
		"whitespace-nowrap",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-offset-1",
		MENU_ITEM_STYLES.size,
		MENU_ITEM_STYLES.gap,
		label ? MENU_ITEM_STYLES.padding : "px-4",
		!label && "justify-center",
		colors.bg,
		colors.text,
		disabled && "opacity-[0.38] pointer-events-none",
		className,
	);

	const containerStyle = {
		transformOrigin: "right",
		borderRadius: `${MENU_ITEM_STYLES.cornerRadius}px`,
	};

	if (asChild) {
		const child = React.Children.only(children) as React.ReactElement<{
			children?: React.ReactNode;
		}>;
		return (
			<m.div variants={MENU_ITEM_VARIANTS} style={containerStyle}>
				<Slot
					role="menuitem"
					tabIndex={tabIndex}
					aria-disabled={disabled ? "true" : undefined}
					data-disabled={disabled ? "true" : undefined}
					onClick={handleClick}
					onPointerDown={onPointerDown}
					onKeyDown={handleKeyDown}
					className={containerClassName}
				>
					{React.cloneElement(child, { children: innerContent })}
				</Slot>
			</m.div>
		);
	}

	return (
		<m.div
			role="menuitem"
			tabIndex={tabIndex}
			aria-disabled={disabled ? "true" : undefined}
			data-disabled={disabled ? "true" : undefined}
			onClick={handleClick}
			onPointerDown={onPointerDown}
			onKeyDown={handleKeyDown}
			variants={MENU_ITEM_VARIANTS}
			style={containerStyle}
			className={containerClassName}
		>
			{innerContent}
		</m.div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// FABMenu Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * MD3 Expressive FAB Menu.
 *
 * Floating Action Button expansion menu. Toggling trigger button reveals vertically stacked action items.
 *
 * Full MD3 Accessibility implementation:
 *  - `role="menu"` assigned to container
 *  - `role="menuitem"` assigned to child action items
 *  - Focus lifecycle management: Open -> Focus first/last menu item; Close -> Restore focus to ToggleFAB
 *  - Keyboard navigation: Escape key closes menu, ArrowUp/Down navigates items, Tab moves focus.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = React.useState(false);
 *
 * const items = [
 *   { id: 'share', icon: <Icon name="share" />, label: 'Share', onClick: () => console.log('Share') },
 *   { id: 'edit', icon: <Icon name="edit" />, label: 'Edit', onClick: () => console.log('Edit') },
 *   { id: 'delete', icon: <Icon name="delete" />, label: 'Delete', disabled: true, onClick: () => {} }
 * ];
 *
 * <FABMenu
 *   expanded={open}
 *   onToggle={setOpen}
 *   aria-label="Quick actions"
 *   alignment="center"
 *   colorVariant="tertiary"

 *   items={items}
 * />
 * ```
 *
 * @see https://m3.material.io/components/floating-action-button/overview
 */
export function FABMenu({
	expanded,
	onToggle,
	items,
	colorVariant = "primary",
	size = "sm",
	alignment = "end",
	className,
	closeOnBackdropClick = true,
	focusLast = true,
	"aria-label": ariaLabel,
}: FABMenuProps) {
	const fabId = React.useId();
	const menuId = React.useId();
	const toggleRef = React.useRef<HTMLButtonElement>(null);
	const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
	const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);

	const reversedItems = React.useMemo(() => [...items].reverse(), [items]);

	const focusItem = React.useCallback((index: number) => {
		const clampedIndex = Math.max(
			0,
			Math.min(index, itemRefs.current.length - 1),
		);
		setFocusedIndex(clampedIndex);
		itemRefs.current[clampedIndex]?.focus();
	}, []);

	// Track whether menu was previously open so we only return focus
	// to the toggle button after a user-initiated close, not on initial mount.
	const wasExpandedRef = React.useRef(false);

	React.useEffect(() => {
		if (expanded) {
			wasExpandedRef.current = true;
			const timer = setTimeout(() => {
				focusItem(focusLast ? items.length - 1 : 0);
			}, FOCUS_DELAY_MS);
			return () => clearTimeout(timer);
		}

		if (wasExpandedRef.current) {
			toggleRef.current?.focus();
		}
		wasExpandedRef.current = false;
		setFocusedIndex(-1);
	}, [expanded, focusLast, items.length, focusItem]);

	const handleMenuKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (!expanded) return;

			const lastIndex = items.length - 1;

			switch (e.key) {
				case "Escape":
					e.preventDefault();
					onToggle(false);
					break;

				case "ArrowUp": {
					e.preventDefault();
					focusItem(focusedIndex <= 0 ? lastIndex : focusedIndex - 1);
					break;
				}

				case "ArrowDown": {
					e.preventDefault();
					focusItem(focusedIndex >= lastIndex ? 0 : focusedIndex + 1);
					break;
				}

				case "Tab": {
					e.preventDefault();
					if (e.shiftKey) {
						focusItem(focusedIndex <= 0 ? lastIndex : focusedIndex - 1);
					} else {
						focusItem(focusedIndex >= lastIndex ? 0 : focusedIndex + 1);
					}
					break;
				}
			}
		},
		[expanded, focusedIndex, items.length, focusItem, onToggle],
	);

	return (
		<LazyMotion features={domMax} strict>
			{expanded && closeOnBackdropClick && (
				<div
					aria-hidden="true"
					className="fixed inset-0 z-40"
					onClick={() => onToggle(false)}
				/>
			)}

			{/* biome-ignore lint/a11y/useSemanticElements: FAB menu container needs div, not fieldset — this is not a form group */}
			<div
				role="group"
				aria-label={ariaLabel ?? "Actions menu"}
				className={cn(
					"fixed z-50 flex flex-col gap-2",
					ALIGNMENT_CONTAINER_CLASSES[alignment],
					"*:shrink-0",
					className,
				)}
				onKeyDown={handleMenuKeyDown}
			>
				<AnimatePresence>
					{expanded && (
						<m.div
							id={menuId}
							role="menu"
							aria-labelledby={fabId}
							aria-orientation="vertical"
							variants={MENU_CONTAINER_VARIANTS}
							initial="closed"
							animate="open"
							exit="closed"
							className={cn(
								"flex flex-col-reverse gap-2",
								ALIGNMENT_ITEMS_CLASSES[alignment],
							)}
						>
							{reversedItems.map((item, reversedIndex) => {
								const originalIndex = items.length - 1 - reversedIndex;
								return (
									<m.div
										key={item.id}
										variants={MENU_ITEM_VARIANTS}
										style={{
											transformOrigin:
												ALIGNMENT_TRANSFORM_ORIGIN[alignment] ?? "right",
										}}
										ref={(el) => {
											itemRefs.current[originalIndex] = el;
										}}
									>
										<FABMenuItem
											icon={item.icon}
											label={item.label}
											onClick={() => {
												if (!item.disabled) {
													item.onClick();
													onToggle(false);
												}
											}}
											disabled={item.disabled}
											colorVariant={colorVariant}
											className={item.className}
											tabIndex={expanded ? 0 : -1}
										/>
									</m.div>
								);
							})}
						</m.div>
					)}
				</AnimatePresence>

				<ToggleFAB
					ref={toggleRef}
					id={fabId}
					expanded={expanded}
					onToggle={onToggle}
					colorVariant={colorVariant}
					size={size}
					aria-label={
						ariaLabel ?? (expanded ? "Close actions menu" : "Open actions menu")
					}
					aria-controls={menuId}
					icon={defaultFabIcon}
				/>
			</div>
		</LazyMotion>
	);
}
